/* global HSCore */

import _ from "lodash";
import React, {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Chart from "react-apexcharts";
import UserContext from "../../../utils/Contexts/UserContext";
import LanguageContext from "../../../utils/Contexts/LanguageContext";
import toast from "react-hot-toast";
import axiosInstance from "../../../utils/Requests/axios";
import TotalBalanceBlue from "../../svg/TotalBalanceBlue";
import TotalBalanceGreen from "../../svg/TotalBalanceGreen";
import ExpectedIncome from "../../svg/ExpectedIncome";
import ExpectedSpending from "../../svg/ExpectedSpending";
import moment from "moment";

function CashFlow() {
  const { userData } = useContext(UserContext);
  const { t, language } = useContext(LanguageContext);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [chartSeries, setChartSeries] = useState([
    { name: "Income", data: [] },
    { name: "Spending", data: [] },
    { name: "Forecast", data: [] },
  ]);

  const [formData, setFormData] = useState({
    search: {
      filters: [{ key: "DATE_RANGE", value: "6" }],
    },
  });

  const allDates = [
    { value: "2", name: t("Last Year") },
    { value: "5", name: t("Last Month") },
    { value: "1", name: t("Current Month") },
    { value: "6", name: t("Current Year") },
    { value: "9", name: t("Next 12 Months") },
  ];

  const getXAxisLabels = () => {
    const selectedRange = Number(formData?.search?.filters[0]?.value);

    if ([6, 2, 9].includes(selectedRange)) {
      return [
        t("Jan"),
        t("Feb"),
        t("Mar"),
        t("Apr"),
        t("May"),
        t("Jun"),
        t("Jul"),
        t("Aug"),
        t("Sep"),
        t("Oct"),
        t("Nov"),
        t("Dec"),
      ];
    }

    return [...cashFlowData]
      .sort((a, b) => new Date(a.date) - new Date(b.date))
      .map((entry) => {
        const day = new Date(entry.date).getDate();
        return day < 10 ? `0${day}` : `${day}`;
      });
  };

  const fetchCashFlow = () => {
    const payload = _.cloneDeep(formData);
    const url = `users/${userData?.uuid}/wallet/cachflow`;
    axiosInstance
      .post(url, payload)
      .then((response) => {
        setCashFlowData(response?.data);
      })
      .catch(() => {
        toast.error(t("There was an error fetching CashFlow data"));
      });
  };
  const localizeMonthOnly = (date, isMonthly = false) => {
    const format = isMonthly ? "MMM YYYY" : "MMM DD, YYYY";
    const formatted = moment(date).format(format); 
    const monthAbbr = moment(date).format("MMM"); 
    const translatedMonth = t(monthAbbr); 

    return formatted.replace(monthAbbr, translatedMonth);
  };

  const previousPayloadRef = useRef(null);
  useEffect(() => {
    const payload = { ...formData };
    if (!_.isEqual(previousPayloadRef.current, payload)) {
      previousPayloadRef.current = payload;
      fetchCashFlow();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const chartOptions = useMemo(() => {
    const greenDot = `
    <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
      <circle cx="7" cy="7" r="7" fill="#02C9A6"/>
    </svg>
  `;
    const blueDot = `
    <svg width="14" height="14" viewBox="0 0 14 14" xmlns="http://www.w3.org/2000/svg">
      <circle cx="7" cy="7" r="7" fill="#387CFF"/>
    </svg>
  `;
    return {
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        background: "transparent",
        events: {
          mouseMove: function (event, chartContext, config) {
            if (config.dataPointIndex !== -1) {
              document
                .querySelectorAll(".apexcharts-xaxis-label")
                .forEach((el) => el.classList.remove("x-label-active"));

              const labelEl = document.querySelectorAll(
                ".apexcharts-xaxis-label"
              )[config.dataPointIndex];
              if (labelEl) labelEl.classList.add("x-label-active");
            }
          },
          mouseLeave: function () {
            document
              .querySelectorAll(".apexcharts-xaxis-label")
              .forEach((el) => el.classList.remove("x-label-active"));
          },
        },
      },
      colors: [
        "rgba(2, 201, 166, 1)",
        "rgba(56, 124, 255, 0.1)",
        "rgba(2, 201, 166, 1)",
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "60%",
          borderRadius: 4,
          columnSpacing: 10,
        },
      },
      grid: {
        borderColor: "rgba(255,255,255,0.02)",
        strokeDashArray: 0,
      },
      dataLabels: { enabled: false },
      stroke: {
        show: true,
        width: [0, 0, 2], // no stroke for bar, 2px for line
        dashArray: [0, 0, 6], // dashed line for forecast
        curve: "straight",
      },
      xaxis: {
        categories: getXAxisLabels(),
        labels: { style: { colors: "#6C747A" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        labels: {
          style: { colors: "#6C747A" },
          formatter: (value) => {
            return `$${Math.round(value).toLocaleString()}`;
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        style: {
          fontFamily: "inherit",
        },
        theme: false,
        custom: ({ series, seriesIndex, dataPointIndex, w }) => {
          const income = series[0][dataPointIndex];
          const spending = series[1][dataPointIndex];

          const selectedRange = Number(formData?.search?.filters[0]?.value);
          const fullDate = new Date(
            cashFlowData[dataPointIndex]?.date || Date.now()
          );

          const formattedDate = localizeMonthOnly(
            fullDate,
            [6, 2, 9].includes(selectedRange)
          );
          const findPreviousValueByType = (typeKey, currentIndex) => {
            const currentType = cashFlowData[currentIndex]?.[typeKey]?.type;
            for (let i = currentIndex - 1; i >= 0; i--) {
              if (cashFlowData[i]?.[typeKey]?.type === currentType) {
                return {
                  value: series[typeKey === "income" ? 0 : 1][i],
                  found: true,
                };
              }
            }
            return { value: null, found: false };
          };

          const formatPercentage = (current, previous) => {
            if (previous === 0 || previous === null || current === null)
              return null;
            const diff = current - previous;
            const percent = (diff / previous) * 100;
            return {
              value: percent.toFixed(1),
              isPositive: percent >= 0,
            };
          };

          const prevIncomeResult = findPreviousValueByType(
            "income",
            dataPointIndex
          );
          const prevSpendingResult = findPreviousValueByType(
            "spending",
            dataPointIndex
          );

          const incomeChange = formatPercentage(income, prevIncomeResult.value);
          const spendingChange = formatPercentage(
            spending,
            prevSpendingResult.value
          );

          const badge = (change) => {
            if (!change) return "";
            const { value, isPositive } = change;
            const bgColor = isPositive
              ? "rgba(2, 201, 166, 0.1)"
              : "rgba(237, 76, 120, 0.1)"; // green / red
            const textColor = isPositive
              ? "rgba(2, 201, 166, 1)"
              : "rgba(237, 76, 120, 1)";
            const prefix = isPositive ? "+" : "-";
            return `
              <span style="
                font-size: 11px;
                padding: 2px 6px;
                border-radius: 5px;
                background-color: ${bgColor};
                color: ${textColor};
                font-weight: 500;
                margin-right: 5px;
              ">
                ${prefix}${Math.abs(value)}%
              </span>
            `;
          };
          return `
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center; gap: 6px;">
                ${greenDot}
                <span>$${income?.toLocaleString()}</span>
              </div>
              ${badge(incomeChange)}
            </div>
            <div style="display: flex; align-items: center; justify-content: space-between;">
              <div style="display: flex; align-items: center; gap: 6px;">
                ${blueDot}
                <span>$${Math.abs(spending)?.toLocaleString()}</span>
              </div>
              ${badge(spendingChange)}
            </div>
            <div style="font-size: 14px; font-weight: 600; color: rgba(108, 116, 122, 1); margin-top: 6px;">
              ${formattedDate}
            </div>
          `;
        },
      },
      states: {
        hover: { filter: { type: "none" } },
        active: { filter: { type: "none" } },
      },
      fill: {
        type: ["solid", "solid", "gradient"],
        gradient: {
          shadeIntensity: 0,
          inverseColors: false,
          opacityFrom: 0,
          opacityTo: 0,
          stops: [0, 0, 0],
        },
      },
      legend: { show: false },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashFlowData, language, formData]);

  useEffect(() => {
    if (cashFlowData?.length) {
      const sortedData = [...cashFlowData].sort(
        (a, b) => new Date(a.date) - new Date(b.date)
      );

      const incomeData = [];
      const spendingData = [];
      const forecastData = [];

      sortedData.forEach((month) => {
        const incomeItem = month.items.find((item) => item.type === 1);
        const spendingItem = month.items.find((item) => item.type === 2);
        const forecastItem = month.items.find((item) => item.type === 3);

        incomeData.push(incomeItem ? incomeItem.amount : 0);
        spendingData.push(spendingItem ? spendingItem.amount : 0);
        forecastData.push(forecastItem ? forecastItem.amount : null);
      });

      setChartSeries([
        { name: "Income", data: incomeData },
        { name: "Spending", data: spendingData },
        { name: "Forecast", data: forecastData },
      ]);
    }
  }, [cashFlowData]);

  const hsInitRef = useRef(null);
  useEffect(() => {
    if (!hsInitRef.current) {
      HSCore.components.HSTomSelect.init(".js-select-flow");
      hsInitRef.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const tomSelectOptionsType = {
    hideSearch: true,
    dropdownParent: "body",
  };

  const [chartHeight, setChartHeight] = useState(265);
  useLayoutEffect(() => {
    const updateHeight = () => {
      setChartHeight(window.innerWidth >= 1920 ? 283 : 265);
    };

    updateHeight();
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  return (
    <div className="card p-3 overflow-hidden cardSetting">
      <div className="d-flex justify-content-between mb-3">
        <h4 className="card-title" style={{ alignSelf: "end" }}>
          {t("Cash Flow")}
        </h4>

        <div className="d-flex gap-5">
          <div className="cash-flow-legends gap-5">
            <span className="d-flex align-items-center gap-1">
              <TotalBalanceGreen />
              <span className="cash-flow-label-text">{t("Income")}</span>
            </span>
            <span className="d-flex align-items-center gap-1">
              <TotalBalanceBlue />
              <span className="cash-flow-label-text">{t("Spending")}</span>
            </span>
            <div
              style={{
                display: "flex",
                alignItems: "center",
              }}
            >
              <span
                style={{
                  width: "2px",
                  height: "28px", // adjust as needed
                  backgroundColor: "rgba(45, 50, 54, 1)",
                }}
              >
                {" "}
              </span>
            </div>
            <span className="d-flex align-items-center gap-1">
              <ExpectedIncome />
              <span className="cash-flow-label-text">
                {t("Expected Income")}
              </span>
            </span>
            <span className="d-flex align-items-center gap-1">
              <ExpectedSpending />
              <span className="cash-flow-label-text">
                {t("Expected Spending")}
              </span>
            </span>
          </div>
          <>
            <ul
              className="nav nav-segment cash-flow-nav align-items-center justify-content-between"
              role="tablist"
            >
              {allDates.map((status) => {
                return (
                  <li className="nav-item contract-nav " key={status.value}>
                    <a
                      className={`js-loading-state-invoker nav-link ${
                        formData.search.filters[0].value === status.value
                          ? "active"
                          : ""
                      }`}
                      id={`tab-${status.value}`}
                      data-bs-toggle="pill"
                      href="#nav-one-eg1"
                      role="tab"
                      aria-controls="nav-one-eg1"
                      aria-selected={
                        formData.search.filters[0].value === status.value
                      }
                      onClick={(e) => {
                        e.preventDefault();
                        setFormData({
                          search: {
                            filters: [
                              { key: "DATE_RANGE", value: status.value },
                            ],
                          },
                        });
                      }}
                      data-hs-loading-state-options='{
            "targetEl": "#nav-one-eg1",
            "removeLoaderDelay": "300"
          }'
                      style={{ paddingTop: "0.2rem", paddingBottom: "0.4rem" }}
                    >
                      {status.name}
                    </a>
                  </li>
                );
              })}
            </ul>

            <div className="tom-select-custom tom-select-cash-flow">
              <select
                className="js-select-flow form-select js-cash-flow"
                autoComplete="off"
                data-hs-tom-select-options={JSON.stringify(
                  tomSelectOptionsType
                )}
                name="type"
                value={formData.search.filters[0].value}
                onChange={(e) => {
                  setFormData({
                    search: {
                      filters: [{ key: "DATE_RANGE", value: e.target.value }],
                    },
                  });
                }}
              >
                {allDates.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.name}
                  </option>
                ))}
              </select>
            </div>
          </>
        </div>
      </div>

      <div
        className="mb-3"
        style={{
          borderBottom: `2px solid var(--border)`,
          marginLeft: "-30px",
          marginRight: "-30px",
        }}
      ></div>

      <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: "842px" }}>
          <Chart
            options={chartOptions}
            series={chartSeries}
            type="bar"
            height={chartHeight}
          />
        </div>
      </div>

      <div
        className="mb-3 cash-flow-divider"
        style={{
          borderBottom: `2px solid var(--border)`,
          marginLeft: "-30px",
          marginRight: "-30px",
        }}
      ></div>
      <div className="cash-flow-legends-sm flex-md-nowrap flex-wrap gap-3 justify-content-md-end">
        {/* Group 1: Income & Spending */}
        <div className="d-flex gap-4 w-100 w-md-auto justify-content-between justify-content-md-start">
          <span className="d-flex align-items-center gap-1">
            <TotalBalanceGreen width={12} />
            <span className="cash-flow-label-text">{t("Income")}</span>
          </span>
          <span className="d-flex align-items-center gap-1">
            <TotalBalanceBlue width={12} />
            <span className="cash-flow-label-text">{t("Spending")}</span>
          </span>
        </div>
        <div className="d-none d-md-flex align-items-center">
          <span
            style={{
              width: "2px",
              height: "28px", // adjust as needed
              backgroundColor: "var(--separator-bg)",
            }}
          >
            {" "}
          </span>
        </div>
        {/* Group 2: Expected Income & Expected Spending */}
        <div className="d-flex gap-4 w-100 w-md-auto justify-content-between justify-content-md-start">
          <span className="d-flex align-items-center gap-1">
            <ExpectedIncome width={12} />
            <span className="cash-flow-label-text">{t("Expected Income")}</span>
          </span>
          <span className="d-flex align-items-center gap-1">
            <ExpectedSpending width={12} />
            <span className="cash-flow-label-text">
              {t("Expected Spending")}
            </span>
          </span>
        </div>
      </div>
    </div>
  );
}

export default CashFlow;
